import React, { useState, useRef } from "react";
import { View, StyleSheet } from "react-native";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtModalError from "../../components/NtModalError";
import { parseError } from "@ntconnect/framework/pbx";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtModal from "../../components/NtModal";
import NtModalAccountPicker from "./NtModalAccountPicker";
import NtToggleRow from "../../components/NtToggleRow";
import { useTheme } from "../../context/ThemeContext";

function NtModalRingGroupDestinationsNewForm({
  onCancel,
  onCreate,
  handleCreateRequest,
}) {
  const { theme } = useTheme();
  const [destination, setDestination] = useState("");
  const [delay, setDelay] = useState("0");
  const [timeout, setTimeout] = useState("30");
  const [enabled, setEnabled] = useState(true);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const accountPickerRef = useRef();

  const isRequiredFields = () => {
    return destination && delay && timeout;
  };

  const handleCreate = async () => {
    if (isRequiredFields() && onCreate) {
      setError(null);
      const params = {
        destination_number: destination,
        destination_delay: delay,
        destination_timeout: timeout,
        destination_enabled: enabled,
      };

      if (handleCreateRequest) {
        setLoading(true);
        const response = await handleCreateRequest(params);
        if (response.ok) {
          onCreate(params);
        } else {
          setError(parseError(response));
        }

        setLoading(false);
      } else {
        onCreate(params);
      }
    }
  };

  const renderLookupAction = () => {
    return (
      <NtTouchableEffect
        style={{
          width: 30,
          height: 30,
          borderRadius: theme.radius,
          backgroundColor: theme.brand,
          alignItems: "center",
          justifyContent: "center",
        }}
        onPress={() => accountPickerRef.current?.show()}
      >
        <NtMaterialIcon name="magnify" color="#ffffff" size={16} />
      </NtTouchableEffect>
    );
  };

  const renderAccountPickerModal = () => {
    return (
      <NtModal ref={accountPickerRef}>
        <NtModalAccountPicker
          removeOptions={["ringgroup", "recording", "voicemail", "other"]}
          defaultSelectedItem={destination}
          onCancel={() => {
            accountPickerRef.current?.dismiss();
          }}
          onPress={(selection) => {
            accountPickerRef.current?.dismiss();
            setDestination(selection?.item?.extension);
          }}
        />
      </NtModal>
    );
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Add new Destination</NtModalTitle>
      <NtModalSubtitle style={[{ marginTop: 6 }]}>
        Add destinations and parameters to the ring group
      </NtModalSubtitle>

      <NtModalInputField
        containerStyle={{ marginTop: 25 }}
        title={"Destination *"}
        placeholder={"Destination"}
        value={destination}
        setValue={(text) => setDestination(text)}
        renderAction={renderLookupAction()}
      />
      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title={"Delay *"}
        placeholder={"Delay"}
        value={delay}
        setValue={(text) => setDelay(text)}
      />
      <NtModalInputField
        containerStyle={{ marginTop: 15 }}
        title={"Timeout *"}
        placeholder={"Timeout"}
        value={timeout}
        setValue={(text) => setTimeout(text)}
      />

      <View style={{ marginTop: 16 }}>
        <NtToggleRow
          value={enabled}
          onChange={setEnabled}
          label="Enable destination"
          sub="When off, this destination won't ring as part of the group."
        />
      </View>

      <NtModalError error={error} containerStyle={{ marginTop: 8 }} />

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-end",
          marginTop: 25,
        }}
      >
        {loading && (
          <NtActivityIndicator
            color={colors.blue}
            size="small"
            containerStyle={{ marginRight: 25 }}
          />
        )}

        <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />

        <NtModalActiveButton
          containerStyle={{ marginLeft: 15 }}
          title={"Create"}
          onPress={() => {
            if (isRequiredFields()) {
              handleCreate();
            }
          }}
          enabled={isRequiredFields()}
        />
      </View>
      {renderAccountPickerModal()}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
  fieldTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: colors.darker,
  },
});

export default NtModalRingGroupDestinationsNewForm;
